import { Label } from "@radix-ui/react-label";
import { useFormContext } from "react-hook-form";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { FormMessage } from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "~/components/ui/popover";
import { Switch } from "~/components/ui/switch";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";
import { CoverImage } from "../cover-image";
import { LocationInput } from "../location-input";
import { TimePicker } from "../time-picker";
import { type EventFormData, previewDateFormatter } from "../utils";

type BasicsStepProps = {
	disabled?: boolean;
};

export function BasicsStep({ disabled }: BasicsStepProps) {
	const form = useFormContext<EventFormData>();
	const watchedFields = form.watch();
	const errors = form.formState.errors;

	return (
		<>
			<div className="flex flex-col space-y-2 md:hidden mb-6">
				<Label className="text-white">Cover Image</Label>
				<CoverImage className="md:hidden" disabled={disabled} />
			</div>
			<div className="flex flex-col space-y-2">
				<Label htmlFor="title" className="text-white">
					Event Title
				</Label>
				<Input
					id="title"
					placeholder="What's the occasion?"
					className={cn(
						"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20",
						disabled && "opacity-50",
					)}
					disabled={disabled}
					{...form.register("title")}
				/>
				{errors.title && (
					<FormMessage className="text-red-500 text-sm mt-1">
						{errors.title.message}
					</FormMessage>
				)}
			</div>
			<div className="flex flex-col space-y-2">
				<Label htmlFor="description" className="text-white">
					Description
				</Label>
				<Textarea
					id="description"
					placeholder="Tell people what this event is about..."
					className={cn(
						"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20 min-h-[160px]",
						disabled && "opacity-50",
					)}
					disabled={disabled}
					spellCheck
					{...form.register("description")}
				/>
				{errors.description && (
					<FormMessage className="text-red-500 text-sm mt-1">
						{errors.description.message}
					</FormMessage>
				)}
			</div>
			<div className="space-y-2">
				<Label className="text-white">Date & Time</Label>
				<div className="flex gap-3">
					<Popover>
						<PopoverTrigger asChild>
							<div className="flex flex-col space-y-1 w-full">
								<Button
									variant="outline"
									className={cn(
										"w-full justify-start text-left font-normal h-12",
										"bg-transparent hover:bg-transparent hover:text-white border-white/10 text-white rounded-xl",
										"hover:border-white/20 transition-colors",
										!watchedFields.startDate && "text-white/50",
										disabled && "opacity-50 pointer-events-none",
									)}
									disabled={disabled}
								>
									{watchedFields.startDate ? (
										previewDateFormatter.format(
											new Date(watchedFields.startDate),
										)
									) : (
										<span>Pick a date</span>
									)}
								</Button>
								{errors.startDate?.message && (
									<FormMessage className="text-red-500 text-sm">
										{errors.startDate?.message}
									</FormMessage>
								)}
							</div>
						</PopoverTrigger>
						<PopoverContent
							className="w-auto p-0 bg-black border border-muted-light/20 backdrop-blur-sm"
							align="start"
						>
							<Calendar
								mode="single"
								selected={
									watchedFields.startDate
										? new Date(watchedFields.startDate)
										: undefined
								}
								onSelect={(date: Date | undefined) => {
									if (date) {
										form.setValue("startDate", date.toISOString());
										form.setError("startDate", { message: "" });
									}
								}}
								disabled={(date: Date) => date < new Date()}
								className={cn(
									"rounded-lg",
									disabled && "opacity-50 pointer-events-none",
								)}
							/>
						</PopoverContent>
					</Popover>
					<TimePicker
						value={watchedFields.startTime}
						onChange={(time: string) => {
							form.setValue("startTime", time);
							form.setError("startTime", { message: "" });
						}}
						error={errors.startTime?.message}
					/>
				</div>
			</div>

			<div className="flex flex-col space-y-2">
				<Label
					htmlFor="locationAddress"
					className="text-white flex items-center gap-2"
				>
					Location Address
				</Label>
				<Input
					id="locationAddress"
					placeholder="123 MG Road, Anytown, India"
					className={cn(
						"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20",
						disabled && "opacity-50",
					)}
					disabled={disabled}
					{...form.register("locationAddress")}
				/>
				{errors.locationAddress && (
					<FormMessage className="text-red-500 text-sm mt-1">
						{errors.locationAddress.message}
					</FormMessage>
				)}
			</div>
			<div className="flex flex-col space-y-2">
				<Label
					htmlFor="locationLink"
					className="text-white flex items-center gap-2"
				>
					Location Link
				</Label>
				<LocationInput disabled={disabled} />
			</div>
			<div className="flex flex-col space-y-2">
				<div className="flex items-center justify-between">
					<div className="space-y-0.5 shrink-0 mr-4">
						<Label className="text-white">Dietary Restrictions</Label>
						<p className="text-sm text-white/60">
							Ask guests to specify their dietary restrictions
						</p>
					</div>
					<Switch
						checked={watchedFields.collectDietaryRestrictions}
						onCheckedChange={(checked) =>
							form.setValue("collectDietaryRestrictions", checked)
						}
						disabled={disabled}
					/>
				</div>
				{errors.collectDietaryRestrictions && (
					<FormMessage className="text-red-500 text-sm">
						{errors.collectDietaryRestrictions.message}
					</FormMessage>
				)}
			</div>

			<h4 className="text-white text-lg font-medium !mt-6 pt-4 border-t border-white/10 border-dotted">
				Guest Settings
			</h4>
			<div className="flex flex-col space-y-2">
				<div className="flex items-center justify-between">
					<div className="space-y-0.5 shrink-0 mr-4">
						<Label className="text-white">Guest Limit</Label>
						<p className="text-sm text-white/60">
							Set a maximum number of guests
						</p>
					</div>
					<Input
						type="text"
						inputMode="numeric"
						className={cn(
							"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20 ml-auto md:w-auto",
							disabled && "opacity-50",
						)}
						placeholder="∞"
						disabled={disabled}
						{...form.register("guestLimit")}
					/>
				</div>
				{errors.guestLimit && (
					<FormMessage className="text-red-500 text-sm">
						{errors.guestLimit.message}
					</FormMessage>
				)}
			</div>
			<div className="flex flex-col space-y-2">
				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label className="text-white">Show Guest List</Label>
						<p className="text-sm text-white/60">
							Make the guest list visible to attendees
						</p>
					</div>
					<Switch
						checked={watchedFields.guestListVisibility}
						onCheckedChange={(checked) =>
							form.setValue("guestListVisibility", checked)
						}
						disabled={disabled}
					/>
				</div>
				{errors.guestListVisibility && (
					<FormMessage className="text-red-500 text-sm">
						{errors.guestListVisibility.message}
					</FormMessage>
				)}
			</div>
			<div className="flex flex-col space-y-2">
				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label className="text-white">Allow Plus Ones</Label>
						<p className="text-sm text-white/60">Let guests bring a friend</p>
					</div>
					<Switch
						checked={watchedFields.allowPlusOne}
						onCheckedChange={(checked) =>
							form.setValue("allowPlusOne", checked)
						}
						disabled={disabled}
					/>
				</div>
				{errors.allowPlusOne && (
					<FormMessage className="text-red-500 text-sm">
						{errors.allowPlusOne.message}
					</FormMessage>
				)}
			</div>
		</>
	);
}
